import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { fmtDate, fmtNum, fmtINR, today } from "@/lib/format";
import { toast } from "sonner";
import { Download, ArrowUpRight, ArrowDownLeft } from "lucide-react";

function ChallanForm({ kind, onDone }) {
  // kind = send_out | receive_back | receive_in | dispatch_back
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [parties, setParties] = useState([]);
  const [parent, setParent] = useState(null);
  const [parents, setParents] = useState([]);
  const [line, setLine] = useState({ item_id: "", qty: 1, rate: 0 });
  const [wh, setWh] = useState("");
  const [partyId, setPartyId] = useState("");

  useEffect(() => { (async () => {
    setItems((await api.get("/items")).data);
    setWarehouses((await api.get("/warehouses")).data);
    setParties((await api.get("/parties")).data);
    if (kind === "receive_back") setParents((await api.get("/job-work/challans", { params: { kind: "send_out" } })).data);
    if (kind === "dispatch_back") setParents((await api.get("/job-work/challans", { params: { kind: "receive_in" } })).data);
  })(); }, [kind]);

  const submit = async () => {
    if (!line.item_id) return toast.error("Pick an item");
    if (!(line.qty > 0)) return toast.error("Qty must be positive");
    try {
      const body = { lines: [line] };
      if (kind === "send_out") {
        if (!partyId) return toast.error("Select job worker");
        if (!wh) return toast.error("Select warehouse");
        Object.assign(body, { job_worker_id: partyId, from_warehouse_id: wh });
      } else if (kind === "receive_back") {
        if (!parent) return toast.error("Select parent send-out");
        if (!wh) return toast.error("Select destination warehouse");
        Object.assign(body, { send_out_id: parent, to_warehouse_id: wh });
      } else if (kind === "receive_in") {
        if (!partyId) return toast.error("Select principal");
        if (!wh) return toast.error("Select destination warehouse");
        Object.assign(body, { principal_id: partyId, to_warehouse_id: wh });
      } else {
        if (!parent) return toast.error("Select parent receive-in");
        if (!wh) return toast.error("Select source warehouse");
        Object.assign(body, { receive_in_id: parent, from_warehouse_id: wh });
      }
      const r = await api.post(`/job-work/${kind.replace("_", "-")}`, body);
      toast.success(`${r.data.challan_no} posted`);
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const isSendType = kind === "send_out" || kind === "dispatch_back";

  return (
    <Card className="p-5 rounded-sm space-y-3">
      <div className="grid md:grid-cols-2 gap-3">
        {["send_out", "receive_in"].includes(kind) && (
          <div><Label>{kind === "send_out" ? "Job worker" : "Principal (customer / owner)"}</Label>
            <Select value={partyId} onValueChange={setPartyId}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="jw-party"><SelectValue placeholder="Select party" /></SelectTrigger>
              <SelectContent>{parties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        {["receive_back", "dispatch_back"].includes(kind) && (
          <div><Label>Parent challan</Label>
            <Select value={parent} onValueChange={setParent}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="jw-parent"><SelectValue placeholder="Select parent" /></SelectTrigger>
              <SelectContent>{parents.map((c) => <SelectItem key={c.id} value={c.id}>{c.challan_no} · {c.job_worker_name || c.principal_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        <div><Label>{isSendType ? "Source warehouse" : "Destination warehouse"}</Label>
          <Select value={wh} onValueChange={setWh}>
            <SelectTrigger className="rounded-sm mt-1" data-testid="jw-wh"><SelectValue placeholder="Select warehouse" /></SelectTrigger>
            <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.code} · {w.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-3 items-end border-t pt-3">
        <div className="col-span-2"><Label>Item</Label>
          <Select value={line.item_id} onValueChange={(v) => setLine({ ...line, item_id: v })}>
            <SelectTrigger className="rounded-sm mt-1" data-testid="jw-item"><SelectValue placeholder="Item" /></SelectTrigger>
            <SelectContent className="max-h-72">{items.map((i) => <SelectItem key={i.id} value={i.id}>{i.sku} · {i.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Qty</Label><Input type="number" value={line.qty} onChange={(e) => setLine({ ...line, qty: Number(e.target.value) })} className="rounded-sm mt-1 text-right tabular-nums" data-testid="jw-qty" /></div>
        <div><Label>Rate</Label><Input type="number" value={line.rate} onChange={(e) => setLine({ ...line, rate: Number(e.target.value) })} className="rounded-sm mt-1 text-right tabular-nums" data-testid="jw-rate" /></div>
      </div>
      <Button onClick={submit} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="jw-submit">Post Challan</Button>
    </Card>
  );
}

function ChallansList({ kind, refresh }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/job-work/challans", { params: { kind } }).then((r) => setRows(r.data)); }, [kind, refresh]);
  return (
    <div className="card-sharp overflow-hidden">
      <table className="w-full dense">
        <thead><tr><th>Challan #</th><th>Date</th><th>Party</th><th className="text-right">Lines</th><th className="text-right">Total Qty</th><th>Status</th></tr></thead>
        <tbody data-testid={`jw-list-${kind}`}>
          {rows.length === 0 && <tr><td colSpan="6" className="text-center text-muted-foreground py-6">No challans yet</td></tr>}
          {rows.map((r) => (
            <tr key={r.id}>
              <td className="font-mono text-xs">{r.challan_no}</td>
              <td className="text-xs">{fmtDate(r.date)}</td>
              <td>{r.job_worker_name || r.principal_name}</td>
              <td className="text-right tabular-nums">{r.lines?.length || 0}</td>
              <td className="text-right tabular-nums">{fmtNum(r.lines?.reduce((s, l) => s + Number(l.qty || 0), 0))}</td>
              <td><span className="text-[10px] font-mono uppercase text-accent">{r.status || "posted"}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ITC04Tab() {
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date(); d.setMonth(d.getMonth() - 3); return d.toISOString().slice(0, 10);
  });
  const [toDate, setToDate] = useState(today());
  const [data, setData] = useState(null);
  const run = async () => {
    try {
      const r = await api.get("/job-work/itc-04", { params: { from_date: fromDate, to_date: toDate } });
      setData(r.data); toast.success("ITC-04 assembled");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-4">
      <Card className="p-4 rounded-sm grid md:grid-cols-4 gap-3">
        <div><Label>From</Label><Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="rounded-sm mt-1" data-testid="itc04-from" /></div>
        <div><Label>To</Label><Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="rounded-sm mt-1" data-testid="itc04-to" /></div>
        <div className="md:col-span-2 flex items-end"><Button onClick={run} className="rounded-sm bg-accent hover:bg-accent/90 text-white w-full" data-testid="itc04-run">Assemble ITC-04</Button></div>
      </Card>
      {data && (
        <>
          <div className="grid grid-cols-3 gap-3">
            <Card className="p-4 rounded-sm"><div className="text-[10px] uppercase font-mono text-muted-foreground">Table 4 · Challans issued</div><div className="text-2xl metric-value mt-1">{data.totals.challans_issued}</div></Card>
            <Card className="p-4 rounded-sm"><div className="text-[10px] uppercase font-mono text-muted-foreground">Table 5A · Received back</div><div className="text-2xl metric-value mt-1">{data.totals.received_back}</div></Card>
            <Card className="p-4 rounded-sm"><div className="text-[10px] uppercase font-mono text-muted-foreground">Send-out value</div><div className="text-2xl metric-value mt-1">{fmtINR(data.totals.total_send_out_value)}</div></Card>
          </div>
          <Card className="p-5 rounded-sm">
            <div className="text-[10px] uppercase font-mono text-accent tracking-widest">Table 4 — Challans issued to job worker</div>
            <table className="w-full dense mt-3">
              <thead><tr><th>Challan</th><th>Date</th><th>Job Worker</th><th className="text-right">Lines</th><th className="text-right">Total Qty</th><th className="text-right">Value</th></tr></thead>
              <tbody>{data.table_4_challans_issued.map((c, i) => (
                <tr key={i}><td className="font-mono text-xs">{c.challan_no}</td><td className="text-xs">{fmtDate(c.date)}</td><td>{c.job_worker}</td><td className="text-right tabular-nums">{c.line_count}</td><td className="text-right tabular-nums">{fmtNum(c.total_qty)}</td><td className="text-right tabular-nums">{fmtINR(c.total_value)}</td></tr>
              ))}</tbody>
            </table>
          </Card>
        </>
      )}
    </div>
  );
}

export default function JobWork() {
  const [refresh, setRefresh] = useState(0);
  const bump = () => setRefresh((r) => r + 1);
  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="jobwork-page">
      <PageHeader eyebrow="Operations" title="Job Work"
        description="Send RM out for job work on returnable challan, receive third-party material for processing, and auto-generate quarterly ITC-04 return with Table 4 / 5A." />
      <Tabs defaultValue="send_out">
        <TabsList className="rounded-sm flex-wrap h-auto">
          <TabsTrigger value="send_out" className="rounded-sm" data-testid="jw-tab-send-out"><ArrowUpRight className="w-3.5 h-3.5 mr-1.5" /> Send Out</TabsTrigger>
          <TabsTrigger value="receive_back" className="rounded-sm" data-testid="jw-tab-receive-back"><ArrowDownLeft className="w-3.5 h-3.5 mr-1.5" /> Receive Back</TabsTrigger>
          <TabsTrigger value="receive_in" className="rounded-sm" data-testid="jw-tab-receive-in"><ArrowDownLeft className="w-3.5 h-3.5 mr-1.5" /> Receive In</TabsTrigger>
          <TabsTrigger value="dispatch_back" className="rounded-sm" data-testid="jw-tab-dispatch-back"><ArrowUpRight className="w-3.5 h-3.5 mr-1.5" /> Dispatch Back</TabsTrigger>
          <TabsTrigger value="itc04" className="rounded-sm" data-testid="jw-tab-itc04"><Download className="w-3.5 h-3.5 mr-1.5" /> ITC-04</TabsTrigger>
        </TabsList>
        {["send_out", "receive_back", "receive_in", "dispatch_back"].map((k) => (
          <TabsContent key={k} value={k} className="mt-4 space-y-4">
            <ChallanForm kind={k} onDone={bump} />
            <ChallansList kind={k} refresh={refresh} />
          </TabsContent>
        ))}
        <TabsContent value="itc04" className="mt-4"><ITC04Tab /></TabsContent>
      </Tabs>
    </div>
  );
}
